import React, { useState, useCallback, useEffect, useRef, useMemo, useContext } from 'react';
import { SmlsButton, SmlsInputText, SmlsModal, SmlsDivider, SmlsInputPassword } from '@smiles/smiles-ui-kit-react';
import TagManager from 'react-gtm-module';
import { Helpers } from '@smiles/core-smiles';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';

import { constants } from '../services/core/config';
import ErrorTypes from '../@types/ErrorTypes';
import { ReCAPTCHA, getLink } from '../utils';

import { ILoginTypes, validateDocumentStatus } from '../services/core/services';
import HyperLink from './Hyperlink/index';
import { useAccountUnderReviewModal } from '../context/AccountUnderReviewModalContext';
import { usePasswordLockModal } from '../context/PasswordLockContext';
import { FIRST_ACCESS_STATUS } from '../utils/DocumentStatusConditions';
import { useFirstAccessModal } from '../context/FirstAccessModalContext';

interface InputErrors {
  EMAIL: string;
  MEMBER: string;
  CPF: string;
}
interface webAuthProps {
  code: string;
}

interface FormDTO {
  hasError: boolean;
  errorType: ErrorTypes;
}

interface FormProps {
  onSubmitFinish(data: FormDTO): void;
  isWifiLogin?: boolean;
  setIdentifierStatus: React.Dispatch<React.SetStateAction<any | null>>;
  setTypeIdentifierService: React.Dispatch<React.SetStateAction<any | null>>;
  setErrorTreatment: React.Dispatch<React.SetStateAction<boolean>>;
  setInvalidDataError: React.Dispatch<React.SetStateAction<boolean>>;
}

interface LoginProps {
  identifier: string;
  password: string;
}

interface validateDocumentStatusProps {
  typeIdentifier: string;
}

const Form: React.FC<FormProps> = ({ onSubmitFinish, isWifiLogin = false, ...props }) => {
  const urlParams = useMemo(() => queryString.parse(window.location.search), []);

  const [identifier, setIdentifier] = useState(() => {
    if (!urlParams.login_hint || typeof urlParams.login_hint !== 'string') {
      return '';
    }
    return urlParams.login_hint.toString();
  });

  const [typeIdentifier, setTypeIdentifier] = useState<undefined | string>(undefined);
  const [password, setPassword] = useState('');
  const [handleSecondStep, setHandleSecondStep] = useState(true);
  const [isValidInput, setIsValidInput] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [recaptchaValue, setRecaptchaValue] = useState('');
  const [isSubmitting, setIsSubmiting] = useState(false);
  const [isSubmitingFirstBtn, setIsSubmitingFirstBtn] = useState(false);
  const [grecaptcha, setGrecaptcha] = useState(window.grecaptcha);
  const { t } = useTranslation('form_component');
  const inputIdentifierRef = useRef<HTMLInputElement>(null);
  const [secondRecaptchaValue, setSecondRecaptchaValue] = useState('');
  const [secondRecaptchaVisible, setSecondRecaptchaVisible] = useState(false);
  const [isRecaptchaVisible, setIsRecaptchaVisible] = useState(false);
  const [isRetrievePasswordVisible, setIsRetrievePasswordVisible] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [needSecondRecaptcha, setNeedSecondRecaptcha] = useState(false);
  const [storageTriesRemain, setStorageTriesRemain] = useState('');
  const [clearPassword, setClearPassword] = useState(false);
  const [siteKey, setSiteKey] = useState<any>();
  const [triesRemain, setTriesRemain] = useState(0);
  const [loginButtonEnabled, setLoginButtonEnabled] = useState(false);
  const [isFirstBtnEnabled, setIsFirstBtnEnabled] = useState(false);
  const [isFirstBtnVisible, setIsFirstBtnVisible] = useState(false);

  const { accountUnderReviewModal } = useAccountUnderReviewModal()
  const { passwordLockModal } = usePasswordLockModal()
  const { firstAccessModal } = useFirstAccessModal()

  const increaseTriesRemain = () => {
    setNeedSecondRecaptcha(true);
    setSecondRecaptchaValue('');
    setSecondRecaptchaVisible(true);
    setPassword('');

    const newTriesRemain = triesRemain + 1;

    sessionStorage.setItem('triesRemain', String(newTriesRemain));
    setTriesRemain(newTriesRemain);

    passwordLockModal.increasePasswordLockCount()
    passwordLockModal.openModal()

    if (triesRemain >= 4) {
      setIdentifier('');
    }
  };

  const getErrorMessage = useCallback((type: 'EMAIL' | 'MEMBER' | 'CPF') => {
    const errors: InputErrors = {
      EMAIL: 'E-mail inválido. Tente novamente.',
      MEMBER: 'Número Smiles inválido. Tente novamente.',
      CPF: 'CPF inválido. Tente novamente.',
    };
    return errors[type];
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (siteKey === undefined) {
      timer = setInterval(() => {
        if (constants.recaptcha) {
          setSiteKey(constants.recaptcha.siteKey);
          clearInterval(timer);
        }
      }, 500);
    }
    return () => {
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (inputIdentifierRef !== null && inputIdentifierRef.current) {
      inputIdentifierRef.current.focus();
    }
  }, [inputIdentifierRef]);

  const handleIdentifierValidation = useCallback(
    (str: string) => {
      sessionStorage.setItem('@Smls:register:user', '');
      sessionStorage.setItem('smilesToken', '');
      setIsRetrievePasswordVisible(false);
      setRecaptchaValue('');
      setIsPasswordVisible(false);
      setPassword('');
      setIsRecaptchaVisible(false);
      setNeedSecondRecaptcha(false);
      setSecondRecaptchaValue('');
      setSecondRecaptchaVisible(false);
      props.setErrorTreatment(false);
      setIsSubmitingFirstBtn(false);

      let inputText = str;
      if (!isNaN(Number(inputText.replace(/[.-]+/g, '')))) {
        inputText = str.replace(/[.-]+/g, '');
      }

      const { isValid, type, mask } = Helpers.validations.isValidLogin(inputText);
      const hasError = !isValid && Boolean(type);

      setTypeIdentifier(type);

      setErrorMessage(hasError ? getErrorMessage(type as any) : '');
      setIdentifier(mask || inputText);
      setIsValidInput(isValid);
      if (isValid) {
        setHandleSecondStep(true);
        setIsRecaptchaVisible(true);
        if (urlParams.bpr) {
          setRecaptchaValue('bpr');
        }
      } else {
        setHandleSecondStep(false);
        setIsRecaptchaVisible(false);
      }

      return !hasError;
    },
    [getErrorMessage],
  );

  const handleLogin = useCallback(
    (formInputs: LoginProps) => {
      props.setErrorTreatment(false);
      setIsSubmiting(true);
      const databaseConnection = 'smilesdb';

      if (typeIdentifier === 'CPF') {
        Object.assign(formInputs, { identifier: formInputs.identifier.replace(/[.-]+/g, '') });
        props.setTypeIdentifierService(typeIdentifier);
      }

      window.webAuth.login(
        {
          realm: databaseConnection,
          username: formInputs.identifier,
          password: formInputs.password,
          captcha_smiles: recaptchaValue,
        },
        (err: webAuthProps) => {
          let errorType: ErrorTypes = 'SERVER_ERROR';

          if (String(err.code).startsWith('invalid_user_password')) {
            console.log('invalid password or user');
            errorType = 'BLOCKED_PASSWORD_ERROR';
            increaseTriesRemain();
            setNeedSecondRecaptcha(true);
            setSecondRecaptchaValue('');
            setRecaptchaValue('');
          } else if (String(err.code).startsWith('smiles.user.email.not.verified')) {
            console.log('caiu no email não verificado');
            errorType = 'UNVERIFIED_EMAIL_ERROR';
            props.setInvalidDataError(true);
            setNeedSecondRecaptcha(true);
            setSecondRecaptchaValue('');
            setRecaptchaValue('');
          } else if (String(err.code).startsWith('too_many_attempts')) {
            console.log('caiu no too_many_attempts');
            props.setErrorTreatment(true);
            errorType = 'BLOCKED_PASSWORD_ERROR';
            setNeedSecondRecaptcha(true);
            setSecondRecaptchaValue('');
            setRecaptchaValue('');
          } else {
            console.log('caiu no else errorTreatment');
            props.setErrorTreatment(true);
          }
          if (urlParams.bpr) {
            setNeedSecondRecaptcha(false);
          }
          window.grecaptcha.enterprise.reset();

          onSubmitFinish({
            hasError: true,
            errorType,
          });

          setPassword('');
          setClearPassword(!clearPassword);
          setRecaptchaValue('');
          setIsSubmiting(false);
          setIsValidInput(false);
        },
      );
    },
    [onSubmitFinish, recaptchaValue, t],
  );

  const getValidateDocumentStatus = async (recaptcha: string) => {
    props.setIdentifierStatus('');
    passwordLockModal.setPasswordLockCount(0)

    setIsSubmitingFirstBtn(true);

    if (typeIdentifier !== undefined) {
      try {
        const response = await validateDocumentStatus({
          recaptcha,
          type: typeIdentifier as ILoginTypes,
          value: identifier,
        });

        const preRegistration = {
          openedSr: response.data?.openedSr || false,
          realStatus: response.data?.realStatus || '',
        }
        
        if(preRegistration.openedSr && ['INCOMPL. REGISTRATION PARTNER', 'PENDING ENROLLMENT'].includes(preRegistration.realStatus)) {
          accountUnderReviewModal.openModal()
          setIsPasswordVisible(false);
          setRecaptchaValue('');
          window.grecaptcha.enterprise.reset();
          setIsSubmitingFirstBtn(false);
          return
        }

        if(!response.data?.lastLogin && FIRST_ACCESS_STATUS.includes(preRegistration.realStatus)) {
          firstAccessModal.openModal();

          firstAccessModal.onClose(() => {
            const passwordLockCount = response.data?.countPasswordLock as number
            const storageTries = passwordLockCount;
            passwordLockModal.setPasswordLockCount(storageTries)

            if (passwordLockCount <= 4) {
              setIsRetrievePasswordVisible(true);
              setIsPasswordVisible(true);
              setIsRecaptchaVisible(false);
              if ([1,2,3].includes(passwordLockCount) && !urlParams.bpr) {
                setNeedSecondRecaptcha(true);
              }
              if (urlParams.bpr) {
                setRecaptchaValue('bpr');
                setSecondRecaptchaValue('bpr');

                setNeedSecondRecaptcha(false);
              }
            }
          });

          setIsSubmitingFirstBtn(false);
          return
        }

        props.setIdentifierStatus(response?.data?.status);
        if (
          (
            response.data?.status === 'ACTIVE' ||
            response.data?.status === 'PVSM SUSPENDED' ||
            response.data?.status === 'SUSPENDED' ||
            response.data?.status === 'MERGED' ||
            response.data?.status === 'INACTIVE' ||
            response.data?.status === 'DECEASED' ||
            response.data?.status === 'REGISTRATION IN ANALYSIS' ||
            response.data?.status === 'CANCELLED' || 
            response.data?.status === 'PRE REGISTRATION' || 
            response.data?.status === 'PENDING ENROLLMENT' || 
            response.data?.status === 'PRE REGISTRATION BANKS' || 
            response.data?.status === 'INCOMPL. REGISTRATION PARTNER'
          ) &&
            response.data?.countPasswordLock !== undefined
        ) {
          const storageTries = response.data?.countPasswordLock;
          sessionStorage.setItem('triesRemain', String(storageTries));
          passwordLockModal.setPasswordLockCount(storageTries)

          if (response.data?.countPasswordLock <= 4) {
            console.log(response.data?.status);
            setIsRetrievePasswordVisible(true);
            setIsPasswordVisible(true);
            setIsRecaptchaVisible(false);
            if ([1,2,3].includes(response.data?.countPasswordLock) && !urlParams.bpr) {
              setNeedSecondRecaptcha(true);
            }
            if (urlParams.bpr) {
              setRecaptchaValue('bpr');
              setSecondRecaptchaValue('bpr');

              setNeedSecondRecaptcha(false);
            }
          } else {
            passwordLockModal.openModal()
            setIsPasswordVisible(false);
            setRecaptchaValue('');
            window.grecaptcha.enterprise.reset();
          }
        } else if (response?.data?.status === undefined || null) {
          setIsPasswordVisible(false);
          setRecaptchaValue('');
          window.grecaptcha.enterprise.reset();
          props.setIdentifierStatus('USER_NOT_FOUND');
        // } else if (
        //   response?.data?.status ===
        //   ('PRE REGISTRATION' ||
        //     'PENDING ENROLLMENT' ||
        //     'PRE REGISTRATION BANKS' ||
        //     'INCOMPL. REGISTRATION PARTNER')
        // ) {
        //   setIsPasswordVisible(false);
        //   setRecaptchaValue('');
        //   window.grecaptcha.enterprise.reset();
        } else {
          setIsPasswordVisible(false);
          setRecaptchaValue('');
          window.grecaptcha.enterprise.reset();
        }
      } catch (e) {
        console.log(e);
      }
    }
    setIsSubmitingFirstBtn(false);
  };

  useEffect(() => {
    if (recaptchaValue !== '' && password.length === 4 && !needSecondRecaptcha) {
      setLoginButtonEnabled(true);
    } else {
      setLoginButtonEnabled(false);
    }
  }, [recaptchaValue, password, needSecondRecaptcha]);

  useEffect(() => {
    if (needSecondRecaptcha && !urlParams.bpr) {
      if (secondRecaptchaValue !== '' && password.length === 4) {
        setLoginButtonEnabled(true);
      } else {
        setLoginButtonEnabled(false);
      }
    }
  }, [secondRecaptchaValue, needSecondRecaptcha, password]);

  useEffect(() => {
    if (urlParams.bpr && password.length === 4) {
      setLoginButtonEnabled(true);
    }
  }, [password]);

  useEffect(() => {
    if (isValidInput && urlParams.bpr) {
      if (typeIdentifier !== '') {
        getValidateDocumentStatus(identifier);
        console.log('bpr1');
      }
    }
  }, [isValidInput]);

  useEffect(() => {
    if (recaptchaValue) {
      setIsFirstBtnEnabled(true);
    } else {
      setIsFirstBtnEnabled(false);
    }
  }, [recaptchaValue]);
  return (
    <>
      <SmlsInputText
        data-testid="input-identifier-form-testid"
        innerRef={inputIdentifierRef}
        inputMode="text"
        id="identifier"
        errorMessage={errorMessage}
        textLabel={t('email_label')}
        placeholder={t('email_placeholder')}
        value={identifier}
        debouncedTime={700}
        onChange={() => {
          setHandleSecondStep(false);
          setIsValidInput(false);
        }}
        validate={handleIdentifierValidation}
      />
      {isPasswordVisible && (
        <SmlsInputPassword
          onlyNumbers
          textLabel={t('password_label')}
          idInput="password"
          onChange={(e) => setPassword(e.target.value)}
          flipFlopClear={clearPassword}
        />
      )}

      {isRecaptchaVisible && (
        <>
          <div className="recaptcha" data-testid="recaptcha-testid-identifier">
            <ReCAPTCHA
              asyncScriptOnLoad={() => setGrecaptcha(window.grecaptcha.enterprise)}
              grecaptcha={grecaptcha}
              sitekey={constants.recaptcha}
              onChange={(value: any) => {
                setRecaptchaValue(value || '');
              }}
              onExpired={() => {
                setRecaptchaValue('');
              }}
            />
          </div>
        </>
      )}
      {!isPasswordVisible && (
        <SmlsButton
          data-testid="submitFirstBtnAfterRecaptcha-testid"
          isLoading={isSubmitingFirstBtn}
          text={t('submit_button_after_recaptcha_continue')}
          disabled={!recaptchaValue || isSubmitingFirstBtn}
          onClick={() => getValidateDocumentStatus(recaptchaValue)}
        />
      )}
      {needSecondRecaptcha && (
        <>
          <div className="recaptcha">
            <ReCAPTCHA
              asyncScriptOnLoad={() => setGrecaptcha(window.grecaptcha.enterprise)}
              grecaptcha={grecaptcha}
              sitekey={constants.recaptcha}
              onChange={(value: any) => {
                setSecondRecaptchaValue(value || '');
                setRecaptchaValue(value || '');
              }}
              onExpired={() => {
                setSecondRecaptchaValue('');
                setRecaptchaValue('');
              }}
            />
          </div>
        </>
      )}

      {isRetrievePasswordVisible && (
        <>
          <SmlsButton
            isLoading={isSubmitting}
            text={t('submit_button_first_step')}
            disabled={
              needSecondRecaptcha
                ? secondRecaptchaValue === '' || password.length !== 4
                : !loginButtonEnabled && password.length !== 4
            }
            onClick={() => {
              if (!handleSecondStep) {
                const dataLayerArgs = {
                  dataLayer: {
                    event: 'pageview',
                    page: '/pagevirtual/login/senha',
                    step: 2,
                  },
                };

                TagManager.dataLayer(dataLayerArgs);
                setHandleSecondStep(true);
                return;
              }
              handleLogin({ identifier, password });
            }}
            block
          />
        </>
      )}
      <div className="hyperlink-login-reset-container">
        <HyperLink
          onClick={() => {
            window.location.href = getLink('recuperar/senha');
          }}
          text={t('forgot_password_button')}
        />
      </div>
    </>
  );
};

export default Form;
